import { AfterViewInit, Directive, ElementRef, Input, Renderer2 } from '@angular/core';

import { BlockAction } from '@core/models';
import { BlockActionType } from '@core/enums';
import { ButtonHandlerService } from '@core/services/button-handler.service';
import { Block } from '@core/models/block/block.model';
import { PageDataType } from '@modules/main/interfaces/page-data-type.interface';
import { ActivatedRoute } from '@angular/router';
import { FeatureFlagsService } from '@core/services/features/feature-flags.service';
import { WallPreviewService } from '@modules/wall-visualization/wall-preview.service';

// AT-9278 #9: akcja „Zobacz na ścianie" za flagą. Klucz jak w backendzie (features API ma
// nagłówek ignore-camel-case-interceptor, więc myślnik nie jest kamelizowany).
const VIEW_ON_WALL_FEATURE = 'view-on-the-wall';

@Directive({
    selector: '[appClickableBlock]',
    standalone: false
})
export class ClickableBlockDirective implements AfterViewInit {
  private _clickableClassName = 'btn-action';
  private clickableAction: BlockAction;
  private block: Block | null;

  constructor(private elementRef: ElementRef,
              private renderer: Renderer2,
              private route: ActivatedRoute,
              private buttonHandlerService: ButtonHandlerService,
              private featureFlags: FeatureFlagsService,
              private wallPreviewService: WallPreviewService) {
  }

  @Input('appClickableBlock') set clickableBlock(block: Block | null) {
    if (!block) {
      return;
    }
    this.block = block;
    this.clickableAction = block.action;
  }

  ngAfterViewInit(): void {
    if (this.hasValidAction()) {
      this.addButtonClickableClass();
      this.addEventListenerToClickableClass();
    }

    if (this.block.action) {
      let locale = this.block.locale;

      if (!locale) {
        const pageData: PageDataType = this.route.snapshot.data.pageData;
        if (pageData) {
          locale = pageData?.page?.locale || 'en';
        }
      }
      this.clickableAction.actionRequestForModel = {
        id: this.block.modelId,
        className: this.block.modelClass,
        ariaLabel: this.block.linkDescription
      };
      if (this.clickableAction.actionType > BlockActionType.NONE
        && this.clickableAction?.linkDescription?.[locale]) {
        this.setAriaLabel(this.clickableAction.linkDescription[locale]);
      }
    }
  }

  private setAriaLabel(ariaLabel: string) {
    this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-label', ariaLabel || null);
  }

  private onClick() {
    if (!this.clickableAction) {
      return;
    }
    this.buttonHandlerService.click$.next(this.clickableAction);
  }

  private onClickEnter(event: any) {
    if (!this.clickableAction) {
      return;
    }
    if (event.key === 'Enter') {
      this.buttonHandlerService.click$.next(this.clickableAction);
    }
  }

  private addButtonClickableClass() {
    this.renderer.addClass(this.elementRef.nativeElement, this._clickableClassName);
  }

  private addEventListenerToClickableClass() {
    this.renderer.listen(this.elementRef.nativeElement, 'click', this.onClick.bind(this));
    this.renderer.listen(this.elementRef.nativeElement, 'keydown', this.onClickEnter.bind(this));
    if (this.clickableAction.actionType === BlockActionType.VIEW_ON_WALL) {
      // AT-9354: dociągnij popup „Zobacz na ścianie", zanim padnie kliknięcie — wtedy fade-in
      // rusza od razu. pointerdown łapie też dotyk, gdzie nie ma hovera.
      for (const eventName of ['pointerenter', 'focus', 'pointerdown']) {
        this.renderer.listen(this.elementRef.nativeElement, eventName, this.prefetchWallPreview.bind(this));
      }
    }
  }

  private prefetchWallPreview() {
    const {actionData, actionRequestForModel} = this.clickableAction;
    if (actionData?.blockId) {
      this.wallPreviewService.prefetch(actionData, actionRequestForModel);
    }
  }

  private hasValidAction(): boolean {
    if (!this.clickableAction) {
      return false;
    }
    const {actionType} = this.clickableAction;
    if (actionType === null) {
      return false;
    }
    // AT-9278 #9: przy wyłączonej fladze element z JUŻ przypiętą akcją VIEW_ON_WALL ma być
    // martwy — bez klasy btn-action (kursor/affordance) i bez listenerów. Inaczej klik otwierał
    // pusty popup (brak danych, bo funkcja wyłączona). To gasi zarówno klikalność, jak i popup
    // (main-page nie dostanie eventu, bo listener się nie podepnie).
    if (actionType === BlockActionType.VIEW_ON_WALL && !this.featureFlags.isEnabled(VIEW_ON_WALL_FEATURE)) {
      return false;
    }
    return actionType !== BlockActionType.NONE;
  }
}
